/**
 * ContactNotesTab Component
 * Journal-style notes for a contact record
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContactNotes,
  createContactNote,
  deleteContactNote,
  type ContactNote,
} from "../../services/contactNotes.service";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../ui/Toast";
import { formatDate } from "../../utils/formatters";
import LoadingSpinner from "../common/LoadingSpinner";

interface ContactNotesTabProps {
  contactId: string;
}

const ContactNotesTab: React.FC<ContactNotesTabProps> = ({ contactId }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch notes
  const {
    data: notes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["contact-notes", contactId],
    queryFn: () => getContactNotes(contactId),
    enabled: !!contactId,
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      return createContactNote(contactId, content, undefined, user?.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-notes", contactId] });
      setNewNote("");
      setIsExpanded(false);
      showToast("success", "Note added successfully");
    },
    onError: () => {
      showToast("error", "Failed to add note");
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: deleteContactNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-notes", contactId] });
      showToast("success", "Note deleted");
    },
    onError: () => {
      showToast("error", "Failed to delete note");
    },
  });

  const handleSubmitNote = () => {
    if (!newNote.trim()) {
      return;
    }
    createNoteMutation.mutate(newNote.trim());
  };

  if (isLoading) {
    return <LoadingSpinner size="md" text="Loading notes..." />;
  }

  if (error) {
    return (
      <div className="text-red-600">Error loading notes. Please try again.</div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>

      {/* Add new note */}
      <div className="mb-6">
        {isExpanded ? (
          <div className="space-y-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note about this contact..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={4}
              autoFocus
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleSubmitNote}
                disabled={!newNote.trim() || createNoteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createNoteMutation.isPending ? "Adding..." : "Add Note"}
              </button>
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setNewNote("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsExpanded(true)}
            className="w-full px-4 py-3 text-left text-gray-500 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-600"
          >
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add a note...
            </div>
          </button>
        )}
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          No notes yet. Add a note to keep track of important information about
          this contact.
        </p>
      ) : (
        <div className="space-y-4">
          {notes.map((note: ContactNote) => (
            <div
              key={note.id}
              className="group relative border-l-4 border-blue-200 bg-gray-50 rounded-r-lg p-4"
            >
              {/* Delete button */}
              <button
                onClick={() => {
                  if (
                    window.confirm("Are you sure you want to delete this note?")
                  ) {
                    deleteNoteMutation.mutate(note.id);
                  }
                }}
                disabled={deleteNoteMutation.isPending}
                className="absolute top-2 right-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete note"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>

              {/* Note content */}
              <p className="text-sm text-gray-800 whitespace-pre-wrap pr-8">
                {note.content}
              </p>

              {/* Note metadata */}
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>
                  {note.creator?.full_name || note.creator?.email || "Unknown"}
                </span>
                <span>•</span>
                <span>{formatDate(note.created_at)}</span>
                {note.support_task_id && (
                  <>
                    <span>•</span>
                    <span className="text-blue-600">From Support Task</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactNotesTab;
